import { useState, useCallback } from 'react';
import { api, getErrorMessage } from '@/api/api';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/context/ToastContext';
import { convertToWebp } from '@/utils/image';

export const useProfilePicture = () => {
  const { user, updateUser } = useAuth();
  const { addToast } = useToast();
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!user) return;

      // Basic validation
      if (file.size > 10 * 1024 * 1024) {
        // 10MB
        addToast({ title: 'Error', subtitle: 'File size must be less than 10MB', type: 'error' });
        return;
      }

      setIsUploading(true);
      try {
        const webpFile = await convertToWebp(file);
        const response = await api.users.uploadProfilePicture(webpFile);

        // Update local user state
        updateUser({ ...user, profile_picture_url: response.profile_picture_url });

        addToast({
          title: 'Success',
          subtitle: 'Profile picture updated successfully.',
          type: 'success',
        });
      } catch (error) {
        addToast({
          title: 'Upload Failed',
          subtitle: getErrorMessage(error),
          type: 'error',
        });
      } finally {
        setIsUploading(false);
      }
    },
    [user, updateUser, addToast]
  );

  const handleDelete = useCallback(async () => {
    if (!user || !user.profile_picture_url) return;

    if (!window.confirm('Are you sure you want to remove your profile picture?')) return;

    setIsUploading(true);
    try {
      await api.users.deleteProfilePicture();
      updateUser({ ...user, profile_picture_url: null }); // Clear locally

      addToast({
        title: 'Success',
        subtitle: 'Profile picture removed.',
        type: 'success',
      });
    } catch (error) {
      addToast({
        title: 'Error',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  }, [user, updateUser, addToast]);

  return { isUploading, handleUpload, handleDelete };
};
